import { DatePipe, NgClass, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MatChipListbox,
  MatChipListboxChange,
  MatChipOption,
} from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { endOfDay, startOfDay, subDays, subMonths } from 'date-fns';
import { identity, pickBy } from 'lodash-es';
import {
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  map,
  switchMap,
  tap,
} from 'rxjs';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { TableLimitService } from '../../services/table-limit.service';
import { AuditLog, AuditLogUser } from './audit-logs.model';
import { AuditLogDetailDialogComponent } from './audit-log-detail-dialog.component';
import { AuditLogsService } from './services/audit-logs.service';

type DateRangeMap = Record<string, (value: Date) => Date>;

const dateRangeMap: DateRangeMap = {
  '7d': (d) => subDays(d, 6),
  '1m': (d) => subMonths(d, 1),
  '3m': (d) => subMonths(d, 3),
};

@Component({
  selector: 'app-audit-logs',
  imports: [
    MatChipListbox,
    MatChipOption,
    MatSelect,
    MatOption,
    MatTableModule,
    MatPaginator,
    MatSort,
    MatSortHeader,
    NgClass,
    DatePipe,
    UpperCasePipe,
    DataTableContainerComponent,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIcon,
    MatIconButton,
    MatInputModule,
    MatTooltip,
    ReactiveFormsModule,
  ],
  providers: [DatePipe],
  templateUrl: './audit-logs.component.html',
  styleUrl: './audit-logs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditLogsComponent implements OnInit {
  private readonly _auditLogs = inject(AuditLogsService);
  private readonly _tableLimit = inject(TableLimitService);
  private readonly _dialog = inject(MatDialog);
  private readonly _datePipe = inject(DatePipe);

  protected readonly dateRangePicker = new FormGroup({
    start: new FormControl(startOfDay(subDays(new Date(), 6))),
    end: new FormControl(endOfDay(new Date())),
  });

  protected readonly maxDate = new Date();

  protected readonly dateRangeOptions = signal([
    { value: '7d', description: 'Afgelopen 7 dagen' },
    { value: '1m', description: 'Afgelopen maand' },
    { value: '3m', description: 'Afgelopen 3 maanden' },
  ]);

  protected readonly dateRangeOptionSelected = signal('7d');

  private readonly _filter = signal({
    resource: [] as string[],
    action: [] as string[],
    userId: [] as string[],
  });

  private readonly _dateRange = signal<{
    startDate: string;
    endDate: string;
  }>({
    startDate: startOfDay(subDays(new Date(), 6)).toISOString(),
    endDate: endOfDay(new Date()).toISOString(),
  });

  private readonly _query = signal({
    page: 1,
    limit: this._tableLimit.getLimit(),
  });

  private readonly _sort = signal({
    sortBy: 'created_at',
    sortOrder: 'desc',
  });

  private readonly _paginator = viewChild.required(MatPaginator);

  private readonly _params$ = combineLatest({
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    filter: toObservable(this._filter),
    dateRange: toObservable(this._dateRange),
  }).pipe(
    map(({ query, sort, filter, dateRange }) => {
      const params: Record<string, string | number | string[]> = {
        ...query,
        ...pickBy(sort, identity),
        ...pickBy(dateRange, identity),
      };
      if (filter.resource.length) params['resource'] = filter.resource;
      if (filter.action.length) params['action'] = filter.action;
      if (filter.userId.length) params['userId'] = filter.userId;
      return params;
    }),
    debounceTime(0)
  );

  private readonly _paginatedLogs$ = this._params$.pipe(
    switchMap((params) => this._auditLogs.getAll({ params })),
    tap(({ meta }) => this._setPaginator({ page: meta.page, total: meta.total })),
    catchError(() => EMPTY)
  );

  private readonly _logs$ = this._paginatedLogs$.pipe(map(({ items }) => items));

  protected readonly dataSource = toSignal(this._logs$, { initialValue: [] });

  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  protected readonly pageSize = computed(() => this._query().limit);

  protected readonly selectedResource = computed(() => this._filter().resource);

  protected readonly selectedAction = computed(() => this._filter().action);

  protected readonly selectedUser = computed(() => this._filter().userId);

  protected readonly resources = toSignal(this._auditLogs.getResources(), {
    initialValue: [],
  });

  protected readonly actions = toSignal(this._auditLogs.getActions(), {
    initialValue: [],
  });

  protected readonly users = toSignal(this._auditLogs.getUsers(), {
    initialValue: [] as AuditLogUser[],
  });

  protected readonly displayedColumns: string[] = [
    'createdAt',
    'displayName',
    'resource',
    'action',
    'status',
  ];

  protected readonly statusMapping: Record<string, string> = {
    success: 'Geslaagd',
    failure: 'Mislukt',
  };

  ngOnInit(): void {
    this.dateRangePicker.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => this._syncDateRange());
  }

  handleDatePickerOptionChange(event: MatChipListboxChange) {
    this.dateRangeOptionSelected.set(event.value);

    const now = new Date();
    const end = endOfDay(now);
    const calculatedStart = dateRangeMap[event.value]
      ? dateRangeMap[event.value](now)
      : now;
    const start = startOfDay(calculatedStart);

    this.dateRangePicker.setValue({ start, end });
  }

  onApplyClicked(): void {
    const dateRange = this.dateRangePicker.value;

    if (dateRange.start && dateRange.end) {
      this.dateRangePicker.setValue({
        start: startOfDay(dateRange.start),
        end: endOfDay(dateRange.end),
      });

      this.dateRangeOptionSelected.set('');
    }
  }

  formatDateRange(): string {
    const start = this.dateRangePicker.value.start;
    const end = this.dateRangePicker.value.end;

    if (!start || !end) {
      return 'Selecteer datumbereik';
    }

    const format = 'd MMM yyyy';
    const startStr = this._datePipe.transform(start, format);
    const endStr = this._datePipe.transform(end, format);

    return `${startStr} – ${endStr}`;
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this._query.update((currentState) => ({
      ...currentState,
      page: pageIndex + 1,
      limit: pageSize,
    }));
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.update((currentState) => ({
      ...currentState,
      sortBy: active,
      sortOrder: direction,
    }));
  }

  handleResourceChange({ value }: MatSelectChange) {
    this._filter.update((currentState) => ({
      ...currentState,
      resource: (value as string[]) ?? [],
    }));

    this._query.update((currentState) => ({
      ...currentState,
      page: 1,
    }));
  }

  handleActionChange({ value }: MatSelectChange) {
    this._filter.update((currentState) => ({
      ...currentState,
      action: (value as string[]) ?? [],
    }));

    this._query.update((currentState) => ({
      ...currentState,
      page: 1,
    }));
  }

  handleUserChange({ value }: MatSelectChange) {
    this._filter.update((currentState) => ({
      ...currentState,
      userId: (value as string[]) ?? [],
    }));

    this._query.update((currentState) => ({
      ...currentState,
      page: 1,
    }));
  }

  openDetailDialog(log: AuditLog) {
    this._dialog.open(AuditLogDetailDialogComponent, {
      data: log,
      width: '960px',
      maxWidth: '95vw',
      maxHeight: '80vh',
    });
  }

  private _syncDateRange() {
    const { start, end } = this.dateRangePicker.value;
    this._dateRange.set({
      startDate: start ? start.toISOString() : '',
      endDate: end ? end.toISOString() : '',
    });
    this._query.update((current) => ({ ...current, page: 1 }));
  }

  private _setPaginator({ page, total }: { page: number; total: number }) {
    const paginator = this._paginator();

    paginator.pageIndex = page - 1;
    paginator.length = total;
  }
}
